import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Shield, Car, User, Phone } from "lucide-react";
import { EditableField } from "./EditableField";

/**
 * Insurance Information Section for Rental Agreement
 */
export const InsuranceSection = ({ insuranceInfo, setInsuranceInfo, isEditing }) => {
  return (
    <Card data-testid="insurance-info-card">
      <CardHeader className="pb-4">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Shield className="w-4 h-4 text-primary" />
          INSURANCE INFORMATION
        </CardTitle>
        <CardDescription>Renter&apos;s own insurance and rental coverage options</CardDescription>
      </CardHeader>
      <CardContent>
        {/* Renter's Own Insurance */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-4">
            {isEditing ? (
              <Checkbox
                id="hasOwnInsurance"
                checked={insuranceInfo.hasOwnInsurance || false}
                onCheckedChange={(checked) => setInsuranceInfo({...insuranceInfo, hasOwnInsurance: checked})}
                data-testid="has-own-insurance-checkbox"
              />
            ) : (
              <div className={`w-5 h-5 rounded flex items-center justify-center ${insuranceInfo.hasOwnInsurance ? 'bg-success/20' : 'bg-muted'}`}>
                {insuranceInfo.hasOwnInsurance && <Shield className="w-3 h-3 text-success" />}
              </div>
            )}
            <label htmlFor="hasOwnInsurance" className="text-sm font-medium cursor-pointer">
              Renter has their own vehicle insurance
            </label>
          </div>
          
          {(insuranceInfo.hasOwnInsurance || isEditing) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg border border-dashed">
              <EditableField 
                label="Insurance Company" 
                value={insuranceInfo.insuranceCompany} 
                isEditing={isEditing}
                onChange={(v) => setInsuranceInfo({...insuranceInfo, insuranceCompany: v})} 
              />
              <EditableField 
                label="Policy Number" 
                value={insuranceInfo.policyNumber} 
                isEditing={isEditing}
                onChange={(v) => setInsuranceInfo({...insuranceInfo, policyNumber: v})} 
              />
              <EditableField 
                label="Policy Holder Name" 
                value={insuranceInfo.policyHolder} 
                isEditing={isEditing}
                onChange={(v) => setInsuranceInfo({...insuranceInfo, policyHolder: v})} 
                icon={<User className="w-3.5 h-3.5" />} 
              />
              <EditableField 
                label="Policy Expiry Date" 
                value={insuranceInfo.policyExpiryDate} 
                isEditing={isEditing} 
                type="date"
                onChange={(v) => setInsuranceInfo({...insuranceInfo, policyExpiryDate: v})} 
              />
              <EditableField 
                label="Coverage Type" 
                value={insuranceInfo.coverageType} 
                isEditing={isEditing}
                onChange={(v) => setInsuranceInfo({...insuranceInfo, coverageType: v})} 
              />
              <EditableField 
                label="Insurance Phone" 
                value={insuranceInfo.insurancePhone} 
                isEditing={isEditing}
                onChange={(v) => setInsuranceInfo({...insuranceInfo, insurancePhone: v})} 
                icon={<Phone className="w-3.5 h-3.5" />} 
              />
            </div>
          )}
        </div>

        <Separator className="my-6" />

        {/* Additional Options */}
        <div>
          <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-4">
            Additional Options
          </h4>
          
          <div className="space-y-4">
            {/* Winter Tires */}
            <div className="flex items-start gap-3 p-3 rounded-lg border bg-card">
              {isEditing ? (
                <Checkbox
                  id="winterTiresSelected"
                  checked={insuranceInfo.winterTiresSelected || false}
                  onCheckedChange={(checked) => setInsuranceInfo({...insuranceInfo, winterTiresSelected: checked})}
                  className="mt-0.5"
                  data-testid="winter-tires-checkbox"
                />
              ) : (
                <div className={`w-5 h-5 rounded flex items-center justify-center mt-0.5 ${insuranceInfo.winterTiresSelected ? 'bg-success/20' : 'bg-muted'}`}>
                  {insuranceInfo.winterTiresSelected && <Car className="w-3 h-3 text-success" />}
                </div>
              )}
              <div className="flex-1">
                <label htmlFor="winterTiresSelected" className="text-sm font-medium cursor-pointer">
                  Winter Tires
                </label>
                <p className="text-xs text-muted-foreground">
                  Vehicle equipped with winter tires for seasonal driving
                </p>
              </div>
              {isEditing ? (
                <div className="flex items-center gap-1">
                  <span className="text-sm text-muted-foreground">$</span>
                  <Input 
                    type="number" 
                    value={insuranceInfo.winterTiresFee || ''} 
                    onChange={(e) => setInsuranceInfo({...insuranceInfo, winterTiresFee: parseFloat(e.target.value) || 0})} 
                    className="h-8 w-20 text-sm" 
                    placeholder="0.00" 
                    data-testid="winter-tires-fee" 
                  />
                  <span className="text-xs text-muted-foreground">fee</span>
                </div>
              ) : (
                insuranceInfo.winterTiresSelected && 
                <span className="text-sm font-medium">${insuranceInfo.winterTiresFee || 0}</span>
              )}
            </div>
          </div>

          {/* Insurance Notes */}
          <div className="mt-4">
            <Label className="text-xs text-muted-foreground uppercase">Notes</Label>
            {isEditing ? (
              <Textarea 
                value={insuranceInfo.insuranceNotes || ''} 
                onChange={(e) => setInsuranceInfo({...insuranceInfo, insuranceNotes: e.target.value})}
                placeholder="Additional notes or special arrangements..."
                className="mt-1"
                rows={2}
              />
            ) : (
              <p className="text-sm text-muted-foreground mt-1">
                {insuranceInfo.insuranceNotes || 'No additional notes.'}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default InsuranceSection;
